import ReactMarkdown from "react-markdown"
import { cn } from "@/lib/cn"
import { safeHref } from "../safe-url"
import type { TextBlock } from "../types"

const TEXT_TONE_CLS: Record<string, string> = {
  muted:   "text-app-muted",
  warning: "text-status-warning",
  danger:  "text-status-error",
}

// Prose with a tiny markdown subset. `disallowedElements` strips
// img/iframe/script/etc; `urlTransform` re-runs the client-side URL
// gate before any anchor tag is attached. No raw HTML, no
// dangerouslySetInnerHTML — partner blob is untrusted-ish.
const DISALLOWED = [
  "img",
  "iframe",
  "script",
  "style",
  "form",
  "input",
  "button",
  "textarea",
  "select",
  "object",
  "embed",
  "video",
  "audio",
  "source",
  "track",
  "canvas",
  "svg",
]

export function TextBlockView({ block }: { block: TextBlock }) {
  const tone = TEXT_TONE_CLS[block.tone ?? ""] ?? "text-app-text"
  return (
    <div className={cn("text-sm leading-relaxed", tone)}>
      <ReactMarkdown
        disallowedElements={DISALLOWED}
        urlTransform={(href) => safeHref(href) ?? "#"}
        components={{
          a: ({ href, children, ...rest }) => {
            const safe = safeHref(href ?? "")
            if (!safe) return <span>{children}</span>
            return (
              <a
                {...rest}
                href={safe}
                target="_blank"
                rel="noopener noreferrer"
                referrerPolicy="no-referrer"
                className="text-brand-red underline-offset-2 hover:underline"
              >
                {children}
              </a>
            )
          },
          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
          code: ({ children }) => (
            <code className="rounded-control bg-black/30 px-1 py-0.5 font-mono text-[12px]">
              {children}
            </code>
          ),
          ul: ({ children }) => <ul className="ml-4 list-disc space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="ml-4 list-decimal space-y-1">{children}</ol>,
        }}
      >
        {block.content ?? ""}
      </ReactMarkdown>
    </div>
  )
}
